import { ChainIDType } from '@starsoft/common/models/chain/id';
import { AxiosError } from 'axios';
import { useMemo } from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useDebounce } from '../../../../common/src/hooks/useDebounce';
import { useWalletAccountsByCoinType } from '../../../../common/src/hooks/wallet/useWalletAccountsByCoinType';
import { useWithdrawSearchParams } from '../../../../common/src/hooks/withdraw/useWithdrawSearchParams';
import {
  useFee,
  useKycMeByOriginId,
  useTokenLookup,
  useWithdrawalMethods,
} from '../../api';
import { useAccountByCoin } from '../../api/account/queries';
import { WithdrawFormCreateDto } from '../../dto/withdrawals/baseWithdrawalInputDto/withdraw-create-form.dto';
import { CommonModalsKey, WithdrawMethodId, WithdrawStep } from '../../enums';
import { Account, Chain, Coin, CoinType, GenericError } from '../../models';
import { TransactionMapping } from '../../models/transaction-mapping';
import { WalletService } from '../../services';
import { getMoneyOrAmount, isValidChainId, parseToSubunits } from '../../utils';
import { useCurrentModal } from '../modals/useCurrentModal';
import { useUserOriginId } from '../user-identity/useUserOriginId';
import { useSelectedWithdrawalMethod } from './useSelectedWithdrawalMethod';

/**
 * Custom hook to manage the withdrawal form state.
 * This hook handles various aspects of the withdrawal form, including fetching necessary data,
 * validating form fields, and managing form state.
 *
 * @param {UseFormWatch<WithdrawFormCreateDto>} watch - Function to watch form fields.
 * @param {UseFormSetValue<WithdrawFormCreateDto>} setValue - Function to set form field values.
 * @returns {Object} An object containing various queries, form state, and utility functions.
 *
 * @example
 * // Usage example:
 * const {
 *   feeQuery,
 *   accountsQuery,
 *   accountByCoinQuery,
 *   tokenLookupQuery,
 *   withdrawMethodsQuery,
 *   selectedWithdrawalMethodQuery,
 *   formError,
 *   refetchForm,
 *   isFormLoading,
 *   useKycMeQuery,
 *   coinOptions,
 *   chainOptions,
 *   isOverValued,
 *   isUnderValued,
 *   maxValueFormatted,
 *   minValueFormatted,
 *   isValidReceiveAmount,
 *   isValidCoinId,
 *   currentChain,
 * } = useWithdrawForm(watch, setValue);
 */
export function useWithdrawForm(
  watch: UseFormWatch<WithdrawFormCreateDto>,
  setValue: UseFormSetValue<WithdrawFormCreateDto>,
) {
  // Retrieve search parameters related to withdrawal
  const { coinId, coinType, transactionStep, withdrawMethodId } =
    useWithdrawSearchParams();
  const originId = useUserOriginId();
  // Watch specific form fields for changes
  const chainId = watch('crypto.chainId');
  const modal: string = useCurrentModal(CommonModalsKey);
  const amount = watch('amount');
  const debouncedAmount = useDebounce(amount);

  // Fetch KYC information based on the withdrawal method and transaction step
  const {
    kyc,
    error: errorKyc,
    refetch: refetchKyc,
    isPending: isPendingKyc,
  } = useKycMeByOriginId({
    enabled:
      [WithdrawMethodId.Crypto?.toString()].includes(withdrawMethodId) ==
        false && transactionStep === WithdrawStep.Form,
  });

  /**
   * Function to handle the fetched coin data.
   * Sets the chain ID if it is valid.
   *
   * @param {Account} data - The fetched account data.
   */
  function handleCoinFetched(data: Account): void {
    if (isValidChainId(data, chainId?.toString())) {
      return;
    }

    setValue(
      'crypto.chainId',
      data?.coin?.chain != null
        ? (data?.coin?.chain?.id as ChainIDType)
        : (data?.coin?.tokens?.[0]?.chain?.id as ChainIDType),
      { shouldDirty: true },
    );
  }

  // Fetch account data by coin ID
  const {
    data: currentAccount,
    error: errorAccountByCoin,
    isLoading: isLoadingAccountByCoin,
    refetch: refetchAccountByCoin,
  } = useAccountByCoin({
    coinId,
    customOnSuccess: handleCoinFetched,
    enabled: transactionStep === WithdrawStep.Form,
  });

  // Fetch fee information based on the amount, coin ID, chain ID, and method ID
  const {
    fee,
    error: errorFee,
    isError: isErrorFee,
    refetch: refetchFee,
    isLoading: isLoadingFee,
  } = useFee({
    amount: parseToSubunits(debouncedAmount, currentAccount?.coin),
    coinId: coinId?.toString(),
    chainId: coinType == CoinType.Crypto ? chainId?.toString() : undefined,
    methodId: withdrawMethodId,
    enabled:
      coinType == CoinType.Crypto
        ? !!chainId &&
          !!coinId &&
          transactionStep !== WithdrawStep.Response &&
          Number(debouncedAmount ?? 0) > 0
        : !!coinId &&
          transactionStep !== WithdrawStep.Response &&
          Number(debouncedAmount ?? 0) > 0,
  });

  // Watch the address field and debounce its value
  const address: string = watch('crypto.address');
  const debouncedAddress: string = useDebounce(address);

  // Fetch token lookup information based on the address, coin ID, and chain ID
  const {
    tokenLookup,
    error: errorTokenLookup,
    refetch: refetchTokenLookup,
    isPending: isPendingTokenLookup,
  } = useTokenLookup({
    address: debouncedAddress,
    coinId: coinId?.toString(),
    chainId: chainId?.toString(),
    enabled:
      coinType == CoinType.Crypto &&
      withdrawMethodId?.toString() === WithdrawMethodId.Crypto.toString() &&
      transactionStep === WithdrawStep.Form,
  });

  // Fetch wallet accounts by coin type
  const {
    accounts,
    error: errorAccounts,
    refetch: refetchAccounts,
    isLoading: isLoadingAccounts,
  } = useWalletAccountsByCoinType({
    coinType,
    enabled: transactionStep === WithdrawStep.Form,
  });

  // Fetch selected withdrawal method
  const {
    withdrawalMethod,
    error: errorWithdrawMethod,
    refetch: refetchWithdrawMethod,
    isLoading: isLoadingWithdrawMethod,
  } = useSelectedWithdrawalMethod({
    enabled: transactionStep === WithdrawStep.Form,
  });

  // Fetch withdrawal methods based on the coin ID and modal key
  const {
    error: errorWithdrawalMethods,
    isLoading: isPendingWithdrawalMethods,
    refetch: refetchWithdrawalMethods,
    ...withdrawMethodsQuery
  } = useWithdrawalMethods({
    enabled:
      transactionStep === WithdrawStep.Form &&
      modal === CommonModalsKey.Withdraw,
    coinId,
  });

  // Determine if the form is loading based on various loading states
  const isFormLoading: boolean =
    isPendingWithdrawalMethods || isLoadingAccounts || isPendingKyc;

  // Aggregate form errors from various queries
  const formError: AxiosError<GenericError> =
    errorWithdrawalMethods ??
    errorWithdrawMethod ??
    errorAccounts ??
    errorAccountByCoin ??
    errorKyc;

  /**
   * Function to refetch form data.
   * Refetches data from various queries if there are errors.
   */
  function refetchForm() {
    if (errorWithdrawalMethods) {
      refetchWithdrawalMethods();
    }

    if (errorWithdrawMethod) {
      refetchWithdrawMethod();
    }

    if (errorAccounts) {
      refetchAccounts();
    }

    if (errorAccountByCoin) {
      refetchAccountByCoin();
    }

    if (errorKyc) {
      refetchKyc();
    }
  }

  // Find the coin mapping based on the withdrawal method and coin ID
  const coinMapping: TransactionMapping = useMemo(
    () =>
      withdrawalMethod?.mappings?.find(
        mapping => mapping?.coin?.id?.toString() == coinId,
      ),
    [withdrawalMethod, coinId],
  );

  // Generate chain options based on the current account and coin type
  const chainOptions: Chain[] = useMemo(() => {
    if (coinType == CoinType.Fiat) {
      return [];
    }

    const options: Chain[] =
      currentAccount?.coin?.tokens?.map(token => token.chain) ?? [];

    if (currentAccount?.coin?.chain) {
      options.push(currentAccount?.coin?.chain);
    }

    return options;
  }, [currentAccount, coinType]);

  // Generate coin options based on the accounts
  const coinOptions: Coin[] = useMemo(() => {
    if (!accounts) return [];

    return accounts.map((account: Account) => account.coin);
  }, [accounts]);

  // Check if the coin ID is valid based on the coin options
  const isValidCoinId: boolean = useMemo(() => {
    if (!coinOptions) {
      return false;
    }

    return coinOptions.some(coin => coin.id?.toString() === coinId);
  }, [coinId, coinOptions]);

  // Format the minimum value based on the coin mapping and current account
  const minValueFormatted: string = WalletService.maskCurrency({
    amount: getMoneyOrAmount(coinMapping?.minimum),
    coin: currentAccount?.coin,
  });

  // Format the maximum value based on the coin mapping and current account
  const maxValueFormatted: string = WalletService.maskCurrency({
    amount: getMoneyOrAmount(coinMapping?.maximum),
    coin: currentAccount?.coin,
  });

  // Check if the amount is overvalued based on the coin mapping
  const isOverValued: boolean = !!coinMapping
    ? getMoneyOrAmount(amount?.toString()) >
      getMoneyOrAmount(coinMapping?.maximum)
    : false;

  // Check if the amount is undervalued based on the coin mapping
  const isUnderValued: boolean = !!coinMapping
    ? getMoneyOrAmount(amount?.toString()) <
      getMoneyOrAmount(coinMapping?.minimum)
    : false;

  // Check if the receive amount is valid based on the fee
  const isValidReceiveAmount: boolean =
    getMoneyOrAmount(fee?.receiveAmount) > 0;

  // Find the current chain based on the chain options and chain ID
  const currentChain = useMemo(
    () =>
      chainOptions?.find(chain => chain?.id?.toString() == chainId?.toString()),
    [chainOptions, chainId],
  );

  return {
    feeQuery: {
      fee,
      errorFee,
      isErrorFee,
      refetchFee,
      isLoadingFee,
    },
    accountsQuery: {
      accounts,
      errorAccounts,
      refetchAccounts,
      isLoadingAccounts,
    },
    accountByCoinQuery: {
      currentAccount,
      errorAccountByCoin,
      isLoadingAccountByCoin,
      refetchAccountByCoin,
    },
    tokenLookupQuery: {
      tokenLookup,
      isPendingTokenLookup,
      errorTokenLookup,
      refetchTokenLookup,
    },
    withdrawMethodsQuery: {
      ...withdrawMethodsQuery,
      isPendingWithdrawalMethods,
    },
    selectedWithdrawalMethodQuery: {
      coinMapping,
      errorWithdrawMethod,
      refetchWithdrawMethod,
      withdrawalMethod,
      isLoadingWithdrawMethod,
    },

    formError,
    refetchForm,
    isFormLoading,
    useKycMeQuery: {
      kyc,
      errorKyc,
      refetchKyc,
      isPendingKyc,
    },
    coinOptions,
    chainOptions,
    isOverValued,
    isUnderValued,
    maxValueFormatted,
    minValueFormatted,
    isValidReceiveAmount,
    isValidCoinId,
    currentChain,
  };
}
